import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Trash2 } from 'lucide-react';

interface SignaturePadProps {
  value?: string; // base64 PNG data URL
  onChange: (base64: string) => void;
  label?: string;
  height?: number;
  disabled?: boolean;
}

export function SignaturePad({
  value,
  onChange,
  label = 'Unterschrift',
  height = 150,
  disabled = false,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(!!value);
  const canvasWidth = useRef(400);

  const drawPlaceholder = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number) => {
    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = '#d0c8be';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(20, h - 30);
    ctx.lineTo(w - 20, h - 30);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#b0a898';
    ctx.font = '13px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText('Hier unterschreiben...', 20, h - 38);
  }, []);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const w = Math.floor(rect.width) || 400;
    canvasWidth.current = w;

    // High-DPI support
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = height * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = height + 'px';

    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);

    if (value && hasDrawn) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, w, height);
        ctx.drawImage(img, 0, 0, w, height);
      };
      img.src = value;
    } else {
      drawPlaceholder(ctx, w, height);
    }
  }, [value, height, hasDrawn, drawPlaceholder]);

  useEffect(() => {
    initCanvas();
    window.addEventListener('resize', initCanvas);
    return () => window.removeEventListener('resize', initCanvas);
  }, [initCanvas]);

  // Load external value changes
  useEffect(() => {
    if (!value) {
      setHasDrawn(false);
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const dpr = window.devicePixelRatio || 1;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          drawPlaceholder(ctx, canvasWidth.current, height);
        }
      }
    } else if (value && !hasDrawn) {
      setHasDrawn(true);
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const img = new Image();
          img.onload = () => {
            const dpr = window.devicePixelRatio || 1;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, canvasWidth.current, height);
            ctx.drawImage(img, 0, 0, canvasWidth.current, height);
          };
          img.src = value;
        }
      }
    }
  }, [value, height, hasDrawn, drawPlaceholder]);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const pos = getPos(e);

    if (!hasDrawn) {
      ctx.clearRect(0, 0, canvasWidth.current, height);
      setHasDrawn(true);
    }

    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext('2d')!;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current!;
    onChange(canvas.toDataURL('image/png'));
  };

  const handleClear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawPlaceholder(ctx, canvasWidth.current, height);
    setHasDrawn(false);
    onChange('');
  };

  return (
    <div className="signature-pad-wrapper">
      {label && <label className="signature-pad-label">{label}</label>}
      <div
        ref={wrapperRef}
        className="signature-pad-canvas-wrap"
        style={{ touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            cursor: disabled ? 'default' : 'crosshair',
            width: '100%',
            height,
          }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </div>
      {!disabled && hasDrawn && (
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          style={{ marginTop: 6 }}
          onClick={handleClear}
        >
          <Trash2 size={12} /> Löschen
        </button>
      )}
    </div>
  );
}
